"use client"

import { useState, useEffect, useRef } from "react"
import { useNavigate } from "react-router"
import { X, Bell, CheckCircle, AlertCircle, Info, Calendar } from "lucide-react"

interface Notification {
  id: string
  type: "success" | "warning" | "info" | "system"
  title: string
  message: string
  time: string
  read: boolean
  action?: {
    label: string
    onClick: () => void
  }
}

interface NotificationsModalProps {
  isOpen: boolean
  onClose: () => void
  notifications?: Notification[]
  onMarkAsRead?: (notificationId: string) => void
  onMarkAllAsRead?: () => void
  position?: "top-right" | "top-left" | "bottom-right" | "bottom-left"
}

export function NotificationsModal({
  isOpen,
  onClose,
  notifications = [],
  onMarkAsRead,
  onMarkAllAsRead,
  position = "top-right",
}: NotificationsModalProps) {
  const modalRef = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()

  // Sample notifications data
  const defaultNotifications: Notification[] = [
    {
      id: "1",
      type: "success",
      title: "Career Path Completed",
      message: "You've successfully completed the Chef career path assessment",
      time: "5 min ago",
      read: false,
      action: {
        label: "View Results",
        onClick: () => console.log("View results clicked"),
      },
    },
    {
      id: "2",
      type: "warning",
      title: "Upcoming Assessment",
      message: "Your Space Explorer Quiz is scheduled for tomorrow",
      time: "1 hour ago",
      read: false,
      action: {
        label: "Prepare",
        onClick: () => console.log("Prepare clicked"),
      },
    },
    {
      id: "3",
      type: "info",
      title: "New Learning Resource",
      message: "New study materials available for Mathematics",
      time: "2 hours ago",
      read: true,
      action: {
        label: "Explore",
        onClick: () => console.log("Explore clicked"),
      },
    },
    {
      id: "4",
      type: "system",
      title: "System Update",
      message: "New features available in your dashboard",
      time: "1 day ago",
      read: true,
    },
  ]

  const displayNotifications = notifications.length > 0 ? notifications : defaultNotifications
  const unreadCount = displayNotifications.filter((n) => !n.read).length

  // Close modal when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        onClose()
      }
    }

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside)
      document.body.style.overflow = "hidden"
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
      document.body.style.overflow = "unset"
    }
  }, [isOpen, onClose])

  // Handle escape key
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose()
      }
    }

    if (isOpen) {
      document.addEventListener("keydown", handleEscape)
    }

    return () => document.removeEventListener("keydown", handleEscape)
  }, [isOpen, onClose])

  const getNotificationIcon = (type: Notification["type"]) => {
    switch (type) {
      case "success":
        return <CheckCircle className="w-5 h-5 text-green-500" />
      case "warning":
        return <AlertCircle className="w-5 h-5 text-amber-500" />
      case "info":
        return <Info className="w-5 h-5 text-blue-500" />
      case "system":
        return <Bell className="w-5 h-5 text-purple-500" />
      default:
        return <Bell className="w-5 h-5 text-gray-500" />
    }
  }

  const getNotificationColor = (type: Notification["type"]) => {
    switch (type) {
      case "success":
        return "bg-green-50 border-green-200"
      case "warning":
        return "bg-amber-50 border-amber-200"
      case "info":
        return "bg-blue-50 border-blue-200"
      case "system":
        return "bg-purple-50 border-purple-200"
      default:
        return "bg-gray-50 border-gray-200"
    }
  }

  const handleNotificationClick = (notification: Notification) => {
    if (!notification.read && onMarkAsRead) {
      onMarkAsRead(notification.id)
    }
    if (notification.action) {
      notification.action.onClick()
    }
  }

  const handleViewAllNotifications = () => {
    onClose() // Close the modal first
    navigate("/notifications") // Navigate to notifications page
  }

  const positionClasses: Record<NotificationsModalProps["position"], string> = {
    "top-right": "top-4 right-4",
    "top-left": "top-4 left-4",
    "bottom-right": "bottom-4 right-4",
    "bottom-left": "bottom-4 left-4",
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50">
      {/* Blurry Backdrop */}
      <div
        className="absolute inset-0 bg-black/30 backdrop-blur-sm transition-opacity duration-300"
        onClick={onClose}
      />

      {/* Notifications Modal */}
      <div
        ref={modalRef}
        className={`
          absolute z-50
          w-96 max-h-[80vh]
          bg-white rounded-xl shadow-2xl border border-gray-200
          flex flex-col
          ${positionClasses[position]}
          animate-in fade-in-0 zoom-in-95 duration-200
        `}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <Bell className="w-5 h-5 text-gray-700" />
            <h3 className="font-semibold text-gray-900">Notifications</h3>
            {unreadCount > 0 && (
              <span className="bg-red-500 text-white text-xs px-2 py-1 rounded-full font-medium">
                {unreadCount} new
              </span>
            )}
          </div>

          <div className="flex items-center gap-2">
            {unreadCount > 0 && onMarkAllAsRead && (
              <button
                onClick={onMarkAllAsRead}
                className="text-sm text-blue-600 hover:text-blue-700 font-medium"
              >
                Mark all read
              </button>
            )}
            <button
              onClick={onClose}
              className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="w-4 h-4 text-gray-500" />
            </button>
          </div>
        </div>

        {/* Notifications List */}
        <div className="flex-1 overflow-y-auto">
          {displayNotifications.length > 0 ? (
            <div className="p-2">
              {displayNotifications.map((notification) => (
                <div
                  key={notification.id}
                  onClick={() => handleNotificationClick(notification)}
                  className={`
                    relative p-3 rounded-lg border cursor-pointer transition-all
                    hover:shadow-md hover:scale-[1.02]
                    ${getNotificationColor(notification.type)}
                    ${!notification.read ? "ring-1 ring-blue-200" : ""}
                    mb-2 last:mb-0
                  `}
                >
                  {/* Unread indicator */}
                  {!notification.read && (
                    <div className="absolute top-3 right-3 w-2 h-2 bg-blue-500 rounded-full" />
                  )}

                  <div className="flex gap-3">
                    {/* Icon */}
                    <div className="flex-shrink-0 mt-0.5">{getNotificationIcon(notification.type)}</div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <h4 className="font-semibold text-gray-900 text-sm mb-1">{notification.title}</h4>
                      <p className="text-gray-600 text-sm mb-2 leading-relaxed">{notification.message}</p>

                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-1 text-xs text-gray-500">
                          <Calendar className="w-3 h-3" />
                          <span>{notification.time}</span>
                        </div>

                        {notification.action && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation()
                              notification.action?.onClick()
                            }}
                            className="text-xs font-medium text-blue-600 hover:text-blue-700 underline"
                          >
                            {notification.action.label}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center p-8 text-center">
              <Bell className="w-12 h-12 text-gray-300 mb-4" />
              <h4 className="font-semibold text-gray-900 mb-2">No notifications</h4>
              <p className="text-gray-500 text-sm">You're all caught up! Check back later for new updates.</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-3 border-t border-gray-200 bg-gray-50 rounded-b-xl">
          <button
            onClick={handleViewAllNotifications}
            className="w-full text-center text-sm text-gray-600 hover:text-gray-800 font-medium py-2 transition-colors hover:bg-gray-100 rounded-lg"
          >
            View All Notifications
          </button>
        </div>
      </div>
    </div>
  )
}